import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { ScheduleFilter } from '../pages/EventPage/ScheduleView/scheduleFilter'
import type { ScheduleSurface } from './analytics'

// The schedule filter lasts for one "event session", scoped per surface so the
// two never leak into each other:
// - event_schedule: in memory while the user stays on the event's routes
//   (tabs, /schedule); leaving the event — or reloading — starts fresh.
// - festival_mode: in localStorage until they exit festival mode, so it
//   survives app restarts mid-festival. Cleared with the saved festival route.

const FESTIVAL_KEY = 'beacon_festival_schedule_filter'
const FILTERS: readonly ScheduleFilter[] = ['none', 'interested', 'going', 'favorites']

interface Slot {
  eventId: string
  filter: ScheduleFilter
}

let eventSlot: Slot | null = null

function readFestivalSlot(): Slot | null {
  try {
    const raw = localStorage.getItem(FESTIVAL_KEY)
    if (!raw) return null
    const slot = JSON.parse(raw) as Slot
    return FILTERS.includes(slot.filter) ? slot : null
  } catch {
    return null
  }
}

function readFilter(eventId: string, surface: ScheduleSurface): ScheduleFilter {
  const slot = surface === 'festival_mode' ? readFestivalSlot() : eventSlot
  return slot?.eventId === eventId ? slot.filter : 'none'
}

function writeFilter(eventId: string, surface: ScheduleSurface, filter: ScheduleFilter) {
  if (surface === 'event_schedule') {
    eventSlot = { eventId, filter }
    return
  }
  try {
    localStorage.setItem(FESTIVAL_KEY, JSON.stringify({ eventId, filter }))
  } catch { /* quota / blocked storage */ }
}

export function clearFestivalScheduleFilter() {
  try { localStorage.removeItem(FESTIVAL_KEY) } catch { /* noop */ }
}

export function useScheduleFilter(eventId: string, surface: ScheduleSurface) {
  const [filter, setFilterState] = useState(() => readFilter(eventId, surface))
  const [prevKey, setPrevKey] = useState(`${surface}:${eventId}`)
  if (prevKey !== `${surface}:${eventId}`) {
    setPrevKey(`${surface}:${eventId}`)
    setFilterState(readFilter(eventId, surface))
  }

  const setFilter = useCallback(
    (next: ScheduleFilter) => {
      writeFilter(eventId, surface, next)
      setFilterState(next)
    },
    [eventId, surface],
  )

  return [filter, setFilter] as const
}

/** Mounted once in RootLayout: ends the event-page filter session on leaving the event's routes. */
export function useScheduleFilterSessionReset() {
  const { pathname } = useLocation()
  useEffect(() => {
    if (!eventSlot) return
    const base = `/events/${eventSlot.eventId}`
    if (pathname !== base && !pathname.startsWith(`${base}/`)) eventSlot = null
  }, [pathname])
}
